import React, { useState } from 'react'
import DragAndDrop from '../../components/DragAndDropUpload'
import CsvFieldMapping from '../../components/CsvFieldMapping'
import GenericModal from '../GenericModal'
import { useQuery, useMutation } from '@tanstack/react-query'
import api from '../../utils/api'
import type { CsvHeader } from '../../models/CsvHeader'
import type { TaskResult } from '../../models/Task'
import { notifyError } from '../../utils/notifyError'
import { notification, closeNotification } from '../Notifications'
import { parseCsvHeaderLine } from './parseCsvHeaderLine'

interface CsvMappingUploadProps {
  isOpen: boolean
  onClose: () => void
  modalTitle: string
  csvHeaderEndpoint: string
  uploadCsvEndpoint: string
  uploadCsvEndpointApiParams?: Record<string, any>
  onSuccess?: () => void
  returnTaskId?: (taskId: string) => void
  hasCreatePermission?: boolean
  importHelpText?: React.ReactNode
}

const CsvMappingUpload: React.FC<CsvMappingUploadProps> = ({ isOpen, modalTitle, onClose, csvHeaderEndpoint, uploadCsvEndpoint, uploadCsvEndpointApiParams, onSuccess = () => { }, returnTaskId, hasCreatePermission = true, importHelpText }) => {
  const { data: backendHeaders, isPending: csvHeaderLoading } = useQuery<CsvHeader[]>({
    queryKey: ['csvheaderapi-' + csvHeaderEndpoint],
    staleTime: Infinity,
    enabled: hasCreatePermission && isOpen,
    queryFn: async () => {
      const result = await api.get(csvHeaderEndpoint)
      return result.data
    }
  })

  const [file, setFile] = useState<File | null>(null)
  const [taskId, setTaskId] = useState<string>('')
  const [userHeaders, setUserHeaders] = useState<string[]>([])
  const [previewData, setPreviewData] = useState<string[][]>([])

  const closeUploadModal = (): void => {
    setTaskId('')
    setFile(null)
    onClose()
  }

  const handleFile = (file: File): void => {
    setFile(file)
    const reader = new FileReader()
    reader.readAsText(file)

    reader.onload = (e) => {
      const csvString = (reader.result ?? '') as string
      const lines = csvString.split('\n')
      const headerLine = lines[0] ?? ''
      setUserHeaders(parseCsvHeaderLine(headerLine))

      // Same header rules for the preview rows, so a quote-wrapped export does not
      // show the quote characters as part of the cell value.
      const previewData = lines.slice(1, 5).map(line => parseCsvHeaderLine(line))
      setPreviewData(previewData)
    }
  }

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (mapping: Record<string, string>): Promise<TaskResult> => {
      const result = await api.post(uploadCsvEndpoint, file, {
        url: uploadCsvEndpoint,
        params: uploadCsvEndpointApiParams,
        headers: {
          'Content-Type': 'text/csv',
          'Classranked-CSV-Mapping': JSON.stringify(mapping)
        }
      })
      return result.data
    }
  })

  const handleUpload = async (mapping: Record<string, string>): Promise<void> => {
    if (file) {
      const toastKey = notification('Uploading...', { variant: 'loading', persist: true })
      try {
        const res = await mutateAsync(mapping)
        closeNotification(toastKey)
        setTaskId(res.taskId)
        if (returnTaskId) {
          returnTaskId(res.taskId)
        }
        setFile(null)
        onSuccess()
      } catch (error) {
        notifyError(error, { fallbackMessage: 'Error uploading CSV. Please try again.' })
        closeNotification(toastKey)
      }
    }
  }

  const parsedModalTitle = taskId ? 'Upload Complete' : `Upload ${modalTitle}`

  function getAlertType (): string | null {
    if (taskId) {
      return 'success'
    }
    return null
  }

  return (
    <GenericModal action={false} maxWidth='sm' title={parsedModalTitle} showCloseIcon={false} isOpen={isOpen} onClose={onClose}>
      <>
        {file === null && <DragAndDrop loading={csvHeaderLoading} taskId={taskId} onDrop={handleFile} onClose={closeUploadModal} alertType={getAlertType()} validationConfig={null} />
        }
        {file !== null && <CsvFieldMapping
          title={modalTitle}
          onClose={() => { setFile(null) }}
          loading={isPending ?? !backendHeaders}
          userHeaders={userHeaders}
          backendHeaders={backendHeaders ?? []}
          previewData={previewData ?? []}
          importHelpText={importHelpText}
          onSuccess={(mapping) => {
            void handleUpload(mapping)
          }}
        />
        }
      </>
    </GenericModal>
  )
}

export default CsvMappingUpload
